import { apiClient, unwrap } from './client';

/**
 * SIMG-COMMERCIAL-LEADBETON-2026-001 — Client API du portail commercial.
 *
 * Le back-office DAF (page /validation) utilise les mêmes endpoints exposés
 * sous /validation par le backend.
 */

// ─── Types ─────────────────────────────────────────────────────

export interface CommercialDashboard {
  proformasARelancer:    number;
  enValidationDaf:       number;
  renvoyeesACorriger:    number;
  commandesEnCours:      number;
  prospectsARecontacter: number;
}

export interface LigneProforma {
  id?:              string;
  produitId:        string;
  quantite:         number;
  prixUnitaireFcfa: number | string;
  remiseLignePct:   number | string;
  totalLigneFcfa:   number | string;
  produit?:         { code: string; designation: string; uniteVente: string };
  notes?:           string | null;
}

/** Proforma / commande — représentation front unifiée. */
export interface Proforma {
  id:                     string;
  reference:              string;
  statut:                 string;
  clientId:               string;
  client?:                { id: string; fullName: string; companyName: string | null; phone: string | null };
  chantierId:             string | null;
  chantier?:              { id: string; nom: string } | null;
  dateCommande:           string;
  dateLivraisonSouhaitee: string | null;
  remisePct:              number | string;
  montantHtFcfa:          number | string;
  poidsTotalKg:           number | string;
  signatureClientUrl:     string | null;
  soumiseValidationLe:    string | null;
  valideeLe:              string | null;
  valideeParUserId:       string | null;
  renvoyeeMotif:          string | null;
  renvoyeeLe:             string | null;
  commercialUserId:       string | null;
  notes:                  string | null;
  lignes:                 LigneProforma[];
  createdAt:              string;
  updatedAt:              string;
}

export interface LigneProformaInput {
  produitId:       string;
  quantite:        number;
  remiseLignePct?: number;
  notes?:          string;
}

export interface CreerProformaInput {
  clientId:                string;
  chantierId?:             string;
  dateLivraisonSouhaitee?: string;
  remisePct?:              number;
  lignes:                  LigneProformaInput[];
  notes?:                  string;
}

export interface ModifierProformaInput {
  dateLivraisonSouhaitee?: string;
  remisePct?:              number;
  lignes?:                 LigneProformaInput[];
  notes?:                  string;
}

/** TICKET-004 C-04 : total de TOUTES les files en attente du DAF ; null = file indisponible (jamais zéro). */
export interface ValidationCounter {
  total: number;
  indisponibles: number;
  parFile: Record<string, number | null>;
}

export interface FileATraiter {
  cle: string; libelle: string; lien: string; etat: 'ok' | 'indisponible'; nombre: number;
  elements: { id: string; reference: string; libelle: string; date: string | null; montantCents: string | null; lien: string }[];
}
export interface ATraiter { files: FileATraiter[]; total: number; indisponibles: number }

export interface KpiVentesMois {
  nbCommandesValidees: number;
  montantHtFcfa:       number;
  nbEnCours:           number;
}

export interface KpiAchatsMois {
  nbBcaValides:  number;
  montantHtFcfa: number;
  nbEnCours:     number;
}

export interface KpiProductionMois {
  nbLotsProduits: number;
  tonnageKg:      number;
  nbOfTermines:   number;
  nbOfEnCours:    number;
}

// ─── Fonctions Commercial ──────────────────────────────────────

export const commercialApi = {
  dashboard: () =>
    apiClient.get('/commercial/dashboard').then(unwrap<CommercialDashboard>),

  mesProformas: (statut?: string) =>
    apiClient
      .get('/commercial/proformas', { params: statut ? { statut } : undefined })
      .then(unwrap<Proforma[]>),

  creerProforma: (body: CreerProformaInput) =>
    apiClient.post('/commercial/proformas', body).then(unwrap<Proforma>),

  modifierProforma: (id: string, body: ModifierProformaInput) =>
    apiClient.patch(`/commercial/proformas/${id}`, body).then(unwrap<Proforma>),

  attacherSignature: (id: string, signatureClientUrl: string) =>
    apiClient
      .post(`/commercial/proformas/${id}/signature`, { signatureClientUrl })
      .then(unwrap<{ id: string; reference: string; signatureClientUrl: string }>),

  soumettreProforma: (id: string) =>
    apiClient.post(`/commercial/proformas/${id}/soumettre`).then(unwrap<Proforma>),

  /**
   * Télécharge le PDF proforma (OHADA) et déclenche le save via un blob download.
   * Le JWT est injecté par l'interceptor axios via `responseType: 'blob'`.
   *
   * Piège : quand l'API renvoie 400 (mentions légales manquantes), axios reçoit
   * le JSON `{error:{code,message}}` sous forme de Blob à cause de responseType.
   * L'interceptor global voit alors `response.data` (blob) sans `.error` et
   * affiche "NETWORK_ERROR". On silence ce toast et on décode le blob à la main.
   */
  telechargerPdf: async (id: string, reference: string) => {
    try {
      const res = await apiClient.get(`/commercial/proformas/${id}/pdf`, {
        responseType: 'blob',
        _silentError: true,
      });
      const disposition = String(res.headers['content-disposition'] ?? '');
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match?.[1] ?? `${reference}.pdf`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return filename;
    } catch (err: any) {
      const blob = err?.response?.data;
      const status = err?.response?.status ?? 'Réseau';
      if (blob instanceof Blob) {
        const text = await blob.text().catch(() => '');
        try {
          const payload = JSON.parse(text);
          const e = payload?.error ?? {};
          const msg = e.message ?? 'Erreur PDF';
          const code = e.code ?? 'PDF_ERROR';
          const { toast } = await import('@/stores/toastStore');
          toast.error(`${code} · ${status}`, msg);
        } catch {
          const { toast } = await import('@/stores/toastStore');
          toast.error(`PDF · ${status}`, text || 'Erreur inconnue');
        }
      }
      throw err;
    }
  },

  mesCommandes: () =>
    apiClient.get('/commercial/commandes').then(unwrap<Proforma[]>),

  mesProspects: () =>
    apiClient.get('/commercial/prospects').then(unwrap<any[]>),
};

// ─── Fonctions Validation DAF (partagées avec P1-C) ────────────

export const validationApi = {
  enAttente: () =>
    apiClient.get('/validation/en-attente').then(unwrap<Proforma[]>),

  count: () =>
    apiClient.get('/validation/a-traiter/compteur').then(unwrap<ValidationCounter>),

  aTraiter: () =>
    apiClient.get('/validation/a-traiter').then(unwrap<ATraiter>),

  validerCommande: (id: string) =>
    apiClient.post(`/validation/commandes/${id}/valider`).then(unwrap<Proforma>),

  renvoyerCommande: (id: string, motif: string) =>
    apiClient.post(`/validation/commandes/${id}/renvoyer`, { motif }).then(unwrap<Proforma>),

  kpiVentesMois: () =>
    apiClient.get('/validation/kpi/ventes-mois').then(unwrap<KpiVentesMois>),

  kpiAchatsMois: () =>
    apiClient.get('/validation/kpi/achats-mois').then(unwrap<KpiAchatsMois>),

  kpiProductionMois: () =>
    apiClient.get('/validation/kpi/production-mois').then(unwrap<KpiProductionMois>),
};
